import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { Title } from '../../../../utilities/interfaces/Title';
import { Agency } from '../../../../utilities/interfaces/Agency';
import { DocumentType } from '../../../../utilities/interfaces/DocumentType';
// Redux slices
import { clearTitleItems, getFilteredTitles } from '../../../../features/state/StateTitle-slice';
import { getFilteredAgencies } from '../../../../features/state/StateAgency-slice';
import { getAllDraftsByTitleId } from '../../../../features/state/StateDraft-slice';
import { Filing } from '../../../../utilities/interfaces/Filing';
import { agencyAPI, draftAPI, filingAPI, filingSupportingDocAPI, titleAPI } from '../../../../utilities/api';
import { ExtendedFilingDetail } from '../../../../utilities/interfaces/ExtendedFilingDetail';
import { useNotificationHelper } from '../../../../utilities/helpers/NotificationHelper';
import { clearDashboardFilings, createFiling, getFilingSupportingDocs, updateFiling } from '../../../../features/state/StateFiling-slice';
import { FilingForm } from '../../../../utilities/interfaces/FilingForm';
import { Draft } from '../../../../utilities/interfaces/Draft';
import { HearingNotice } from '../../../../utilities/interfaces/HearingNotice';
import { createHearingNotice } from '../../../../features/state/StateHearingNotice-slice';
import { getFilingActionsById } from '../../../../features/state/StateDocumentTypeFilingAction-slice';
import { getDocumentTypes } from '../../../../features/state/StateDocumentType-slice';
import { getAllWorkflows } from '../../../../features/state/StateWorkflow-slice';
import { Workflow } from '../../../../utilities/interfaces/Workflow';
import { FilingWorkflowHistory } from '../../../../utilities/interfaces/FilingWorkflowHistory';
import { getFilingWorkflowHistoriesByFilingId } from '../../../../features/state/StateFilingWorkflowHistory-slice';
import { workflowStatusApi } from '../../../../utilities/api/workflowStatusApi';
import { getWorkflowStatusActionsByStatusAndRoleIds } from '../../../../features/state/StateWorkflowStatusAction-slice';
import { Roles } from '../../../../utilities/enums/Roles';
import { AdvanceFiling } from '../../../../utilities/interfaces/AdvanceFiling';
import { filingNoteApi } from '../../../../utilities/api/filingNoteApi';
import { FilingNote } from '../../../../utilities/interfaces/FilingNote';
import { WorkflowStatuses } from '../../../../utilities/enums/WorkflowStatuses';
import { clearDocket } from '../../../../features/state/StateDocket-slice';
import { PDFExport } from '@progress/kendo-react-pdf';

export const useFilingFormController = (
  closeFilingForm: any,
  filingFormData: FilingForm,
  filing?: Filing
) => {
  // REDUX
  const titles: Title[] = useAppSelector((state) => state.titles.filteredTitleList);
  const agencies: Agency[] = useAppSelector((state) => state.agency.filteredAgencies);
  const workflows: Workflow[] = useAppSelector((state) => state.workflows.workflows);
  const drafts: Draft[] = useAppSelector((state) => state.draft.filingDrafts);
  const documentTypes: DocumentType[] = useAppSelector((state) => state.documentType.documentTypes);
  const histories: FilingWorkflowHistory[] = useAppSelector((state) => state.filingWorkflowHistories.histories);
  const filingActions: any[] = useAppSelector((state) => state.documentTypeFilingAction.filingActions);
  const appUser: any = useAppSelector((state) => state.appUser);
  const authRoles :any = useAppSelector((state) => state.authRoles.roles);
  const savingFiling: boolean = useAppSelector((state) => state.filings.savingFiling);
   const filingCertificateOfAdoptionDocs = useAppSelector((state) => state.filings.filingCertificateOfAdoptionDocs);
  const dispatch = useAppDispatch();

  // notification helper (this helper stores items in the redux store)
  const { addNotification } = useNotificationHelper();

  // #region formValues
  // State
  const [newHearingNotice, setNewHearingNotice] = useState<boolean>(false);
  const [status, setStatus] = useState<any>();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [workflowLock, setWorkflowLock] = useState<boolean>(false);
  const [hearingLock, setHearingLock] = useState<boolean>(false);
  const [draftName, setDraftName] = useState<string>('');
  const pdfExport = useRef<PDFExport>(null);

  let getInitialState = (filing?: Filing) => ({
    title: {},
    agency: {},
    chapter: {},
    action: {},
    ruleChangeType: {},
    drafts: filing?.filingDetail?.drafts ?? [],
    filingName: filing?.filingName ?? '',
    createdBy: filing?.createdBy ?? '',
    effectiveOn: filing?.effectiveOn ?? new Date(),
    expiresOn: filing?.expiresOn ?? new Date(),
    filedOn: filing?.filedOn ?? new Date(),
    additionalAgencyEmail: '',
    affiliationOfPersonCommenting: null,
    agencyId: null,
    titleId: null,
    chapterId: null,
    agencyEmail: null,
    agencyName: null,
    agencyNotes: null,
    agencyPhone: null,
    agencyResponse: null,
    certificationStatement: null,
    changesBetweenNoticeAndHearing: null,
    chapterSubject: null,
    descriptionAndSummaryOfRuleChanges: null,
    directorsSignature: null,
    fteImpact: null,
    hearingComments: null,
    hearingDescription: null,
    hearingAddressLine1: null,
    hearingAddressLine2: null,
    hearingCity: null,
    hearingDate: new Date(),
    hearingState: null,
    hearingPostalCode: null,
    hearingEMeetingInfo: null,
    hearingSummaryContainerName: null,
    hearingSummaryBlobName: null,
    issuesOfUniqueInterest: null,
    isUnfundedMandate: null,
    nameOfPersonCommenting: null,
    nameOfPersonSigningCertificate: null,
    preambleAndCertificateContainerName: null,
    preambleAndCertificateBlobName: null,
    preparerContactInfo: null,
    reasonForAdoptionAmendmentRepeal: null,
    explanatoryDate: new Date(),
    ruleChangeTypeId: null,
    timeOfHearing: null,
    titleOfSigner: null,
    titleSubject: null,
    workflow: {},
    fiscalImpactStatementCreatedOn: new Date(),
    indeterminableImpactDesc: null,
    politicalSubdivisionImpactDesc: null,
    regulatedPublicImpactDesc: null,
    stateAgencyImpactDesc: null,
    politicalSubdivisionHasNoFiscalImpact: false,
    politicalSubdivisionHasIncreasedCost: false,
    politicalSubdivisionHasDecreasedCost: false,
    politicalSubdivisionHasIncreasedRevenue: false,
    politicalSubdivisionHasDecreasedRevenue: false,
    politicalSubdivisionIsIndeterminable: false,
    regulatedPublicHasNoFiscalImpact: false,
    regulatedPublicHasIncreasedCost: false,
    regulatedPublicHasDecreasedCost: false,
    regulatedPublicHasIncreasedRevenue: false,
    regulatedPublicHasDecreasedRevenue: false,
    regulatedPublicIsIndeterminable: false,
    stateAgencyHasNoFiscalImpact: false,
    stateAgencyHasIncreasedCost: false,
    stateAgencyHasDecreasedCost: false,
    stateAgencyHasIncreasedRevenue: false,
    stateAgencyHasDecreasedRevenue: false,
    stateAgencyIsIndeterminable: false,
    //attachments
    hearingNoticeAttachments: filing?.filingDetail?.hearingNoticeAttachments ?? [],
    hearingSummaryAttachments: filing?.filingDetail?.hearingSummaryAttachments ?? [],
    affidavitOfPublicationAttachments:
      filing?.filingDetail?.affidavitOfPublicationAttachments ?? [],
  });
  // #endregion

  const useFormState = (initialState: object = {}) => {
    const [formValues, setFormValues] = useState(initialState);

    const handleChange: any = (e: any, name: string = '', value: any) => {
      setFormValues((values) => ({
        ...values,
        [name]: value,
      }));

      if (name === 'agency') {
        dispatch(getFilteredTitles(value.id));
      }

      if (name === 'title') {
        dispatch(getAllDraftsByTitleId(value.id));
      }
    };

    return [formValues, handleChange];
  };

  const [formValues, handleChange] = useFormState({
    ...getInitialState(filing),
    ...filing?.filingDetail,
  });

  const [draftDisabled, setDraftDisabled] = useState<boolean>(formValues?.workflow?.id && formValues.title?.id);

  useEffect(() => {
    if (filing?.id && histories.length < 1) {
      dispatch(getFilingWorkflowHistoriesByFilingId(filing.id));
    }
    if (filing?.id && !status && filing?.workflowStatusId) {
      getStatus(filing.workflowStatusId);
    }
    if (filing?.id) {
      dispatch(getFilingSupportingDocs(filing.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filing])

  const getStatus = async (id: number) => {
    let result = await workflowStatusApi.getById(id);
    setStatus(result);
  }

  useEffect(() => {
    if (!draftDisabled && formValues.workflow?.id && formValues.title?.id) {
      setDraftDisabled(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.workflow, formValues.title])

  useEffect(() => {
    if (titles.length < 1) {
      dispatch(getFilteredAgencies());
    }
    if (filing?.id && !formValues.agency.id && !formValues.title.id && !formValues.chapter.id) {
      populateFilingDropDowns();
    }
    if (documentTypes.length < 1) {
      dispatch(getDocumentTypes());
    }
    if (workflows.length < 1) {
      dispatch(getAllWorkflows());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, titles.length]);

  useEffect(() => {
    if (filing?.workflowId && workflows.length > 0) {
      formValues.workflow = workflows.find(x => x.id === filing.workflowId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filing?.workflowId, workflows])

  const populateFilingDropDowns = async () => {
    let selectedAgency = await agencyAPI.getAgencyByAgencyId(formValues.agencyId);
    let selectedTitle = await titleAPI.getTitleById(filing!.titleId);
    handleChange(formValues, 'agency', selectedAgency);
    handleChange(formValues, 'title', selectedTitle);
  };

  // #region onChange handlers
  const handleFormChange = (name: string, value: any) => {
    handleChange(formValues, name, value);
  };

  const datePickerChange = (name: string, event: any) => {
    let date = new Date(event.value);
    handleChange(formValues, name, date);
  };

  const datePickerDateChange = (name: string, event: any) => {
    let currentDate = new Date(formValues[name]);
    let newDate = new Date(event.value);
    currentDate.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
    handleChange(formValues, name, currentDate);
  }

  const datePickerTimeChange = (name: string, event: any) => {
    let currentDate = new Date(formValues[name]);
    let newDate = new Date(event.value);
    currentDate.setHours(newDate.getHours(), newDate.getMinutes());
    handleChange(formValues, name, currentDate);
  }

  const checkBoxChange = (name: string) => {
    formValues[name] ? handleChange(formValues, name, false) : handleChange(formValues, name, true);
  };

  const selectChange = (type: string, data: any) => {
    if (type === 'agencySelect') {
      handleChange(formValues, 'agency', data);
      handleChange(formValues, 'title', {});
      handleChange(formValues, 'chapter', {});
      handleMultiSelectChange([]);
      dispatch(clearTitleItems());
      dispatch(getFilteredTitles(data.id));
    }
    if (type === 'titleSelect') {
      handleChange(formValues, 'title', data);
      handleChange(formValues, 'chapter', {});
      dispatch(getAllDraftsByTitleId(data.id));
    }
    if (type === 'action') {
      handleChange(formValues, 'action', data);
    }
    if (type === 'ruleChangeType') {
      handleChange(formValues, 'ruleChangeType', data);
    }
    if (type === 'workflowSelect') {
      handleChange(formValues, 'workflow', data);
      handleMultiSelectChange([]);
    }
  };

  const handleMultiSelectChange = (values: any) => {
    handleFormChange('drafts', values);
  };
  // #endregion

  // #region getters
  const getFormValue = (name: string) => {
    return formValues[name];
  };

  const getCheckboxValue = (name: string) => {
    return formValues[name];
  };

  const getDropDownValues = (name: string) => {
    if (name === 'agencySelect') {
      if(authRoles.includes("Admin" || "SOS Office"))
        return agencies;

      return agencies.filter((agency) =>
        appUser.appUserAvailableAgencies.payload.includes(agency.agencyName)
      );
    }
    if (name === 'titleSelect') {
      if(authRoles.includes("Admin" || "SOS Office"))
        return titles;

      return titles.filter((title) =>
        appUser.appUserCurrentTitles.payload.includes(title.titleNumber.toString())
      );
    }
    if (name === 'chapterSelect') {
      // return chapters;
      return [];
    }
    if (name === 'workflowSelect') {
      return workflows;
    }
    if (name === 'action' || name === 'ruleChangeType') {
      return filingActions;
    }

    return [];
  };

  const getMultiSelectValues = (name: string) => {
    if (name === 'draftSelect') {
      return drafts.filter(x => x.workflowId === formValues.workflow.id && !x.isPromulgated);
    }
  };

  const getDropdownLabelValue = (name: string) => {
    switch (name) {
      case 'agencySelect':
        return 'agencyName';
      case 'titleSelect':
        return 'titleName';
      case 'chapterSelect':
       return 'chapterName';
      case 'action':
        return 'filingActionName';
      case 'ruleChangeType':
       return 'filingActionName';
      case 'workflowSelect':
        return 'workflowName';
      default:
        return 'Title';
    }
  };

  const getSelectedDropDownItem = (name: string) => {
    switch (name) {
      case 'agencySelect':
        return formValues.agency;
      case 'titleSelect':
        return formValues.title;
      case 'chapterSelect':
        return formValues.chapter;
      case 'action':
        return formValues.action;
      case 'ruleChangeType':
        return formValues.ruleChangeType;
      case 'workflow':
        return formValues.workflow;
      default:
        break;
    }
  };

  const getDropdownPrefix = (name: string) => {
    switch (name) {
      case 'agencySelect':
        return '';
      case 'titleSelect':
        return 'Title Number';
      case 'chapterSelect':
        return 'Chapter Number';
      default:
        break;
    }
  };

  const getDropdownId = (name: string) => {
    switch (name) {
      case 'agencySelect':
        return '';
      case 'titleSelect':
        return 'titleNumber';
      case 'chapterSelect':
        return 'chapterNumber';
      case 'action':
        return 'id';
      case 'ruleChangeType':
        return 'id';
      case 'workflowSelect':
        return 'id';
      default:
        break;
    }
  };

  const getDropdownValueField = (name: string) => {
    switch (name) {
      case 'agencySelect':
        return 'agency';
      case 'titleSelect':
        return 'title';
      case 'chapterSelect':
        return 'chapter';
      case 'action':
        return 'action';
      case 'ruleChangeType':
        return 'ruleChangeType';
      case 'workflowSelect':
        return 'workflow';
      default:
        break;
    }
  }
  // #endregion

  const [statusActionId, setStatusActionId] = useState<number>();

  const toggleConfirmationDialog = (confirmed: boolean = false) => {
    setDialogOpen(!dialogOpen);
    if (confirmed) {
      if (workflowLock) {
        tryCreateFiling();
      }
      if (hearingLock && filing?.id && statusActionId) {
        performAction(filing?.id, statusActionId);
        setStatusActionId(undefined);
      }
    }
  }

  useEffect(() => {
    if (!filing?.id) {
      setWorkflowLock(true);
    }
    else {
      setWorkflowLock(false);
    }
  }, [filing?.id])

  useEffect(() => {
    if (formValues?.workflow?.id === 1 && status?.id === 1) {
      setHearingLock(true);
    }
    else {
      setHearingLock(false);
    }
  }, [formValues?.workflow?.id, status?.id])

  const hearingLockFields: string[] = ['Hearing Notice tab', 'Hearing Summary Tab'];
  const workflowLockFields: string[] = ['Workflow'];
  // #endregion

  // #region Save Filing
  let getFilingDetail = (formValues: any): ExtendedFilingDetail => ({
    drafts: formValues.drafts ?? [],
    additionalAgencyEmail: formValues.additionalAgencyEmail ?? '',
    affiliationOfPersonCommenting: formValues.affiliationOfPersonCommenting ?? '',
    agencyId: formValues.agency.id?.toString() ?? '',
    agencyEmail: formValues.agencyEmail ?? '',
    agencyName: formValues.agency.name ?? '',
    agencyNotes: formValues.agencyNotes ?? '',
    agencyPhone: formValues.agencyPhone ?? '',
    agencyResponse: formValues.agencyResponse ?? '',
    certificationStatement: formValues.certificationStatement ?? '',
    changesBetweenNoticeAndHearing: formValues.changesBetweenNoticeAndHearing ?? '',
    chapterSubject: formValues.chapterSubject ?? '',
    descriptionAndSummaryOfRuleChanges: formValues.descriptionAndSummaryOfRuleChanges ?? '',
    directorsSignature: formValues.directorsSignature ?? '',
    fteImpact: formValues.fteImpact ?? '',
    hearingAddressLine1: formValues.hearingAddressLine1 ?? '',
    hearingAddressLine2: formValues.hearingAddressLine2 ?? '',
    hearingCity: formValues.hearingCity ?? '',
    hearingComments: formValues.hearingComments ?? '',
    hearingDate: formValues.hearingDate ?? new Date(),
    hearingState: formValues.hearingState ?? '',
    hearingZip: formValues.hearingZip ?? '',
    hearingEMeetingInfo: formValues.hearingEMeetingInfo ?? '',
    hearingSummaryContainerName: formValues.hearingSummaryContainerName ?? '',
    hearingSummaryBlobName: formValues.hearingSummaryBlobName ?? '',
    issuesOfUniqueInterest: formValues.issuesOfUniqueInterest ?? '',
    isUnfundedMandate: formValues.isUnfundedMandatefalse ?? false,
    nameOfPersonCommenting: formValues.nameOfPersonCommenting ?? '',
    nameOfPersonSigningCertificate: formValues.nameOfPersonSigningCertificate ?? '',
    preambleAndCertificateContainerName: formValues.preambleAndCertificateContainerName ?? '',
    preambleAndCertificateBlobName: formValues.preambleAndCertificateBlobName ?? '',
    preparerContactInfo: formValues.preparerContactInfo ?? '',
    reasonForAdoptionAmendmentRepeal: formValues.reasonForAdoptionAmendmentRepeal ?? '',
    ruleChangeTypeId: formValues.ruleChangeType.id,
    timeOfHearing: formValues.timeOfHearing ?? '',
    titleOfSigner: formValues.titleOfSigner ?? '',
    titleSubject: formValues.titleSubject ?? '',
    fiscalImpactStatementCreatedOn: formValues.fiscalImpactStatementCreatedOn ?? new Date(),
    indeterminableImpactDesc: formValues.indeterminableImpactDesc ?? '',
    politicalSubdivisionImpactDesc: formValues.politicalSubdivisionImpactDesc ?? '',
    regulatedPublicImpactDesc: formValues.regulatedPublicImpactDesc ?? '',
    stateAgencyImpactDesc: formValues.stateAgencyImpactDesc ?? '',
    politicalSubdivisionHasNoFiscalImpact: formValues.politicalSubdivisionHasNoFiscalImpact ?? false,
    politicalSubdivisionHasIncreasedCost: formValues.politicalSubdivisionHasIncreasedCost ?? false,
    politicalSubdivisionHasDecreasedCost: formValues.politicalSubdivisionHasDecreasedCost ?? false,
    politicalSubdivisionHasIncreasedRevenue: formValues.politicalSubdivisionHasIncreasedRevenue ?? false,
    politicalSubdivisionHasDecreasedRevenue: formValues.politicalSubdivisionHasDecreasedRevenue ?? false,
    politicalSubdivisionIsIndeterminable: formValues.politicalSubdivisionIsIndeterminable ?? false,
    regulatedPublicHasNoFiscalImpact: formValues.regulatedPublicHasNoFiscalImpact ?? false,
    regulatedPublicHasIncreasedCost: formValues.regulatedPublicHasIncreasedCost ?? false,
    regulatedPublicHasDecreasedCost: formValues.regulatedPublicHasDecreasedCost ?? false,
    regulatedPublicHasIncreasedRevenue: formValues.regulatedPublicHasIncreasedRevenue ?? false,
    regulatedPublicHasDecreasedRevenue: formValues.regulatedPublicHasDecreasedRevenue ?? false,
    regulatedPublicIsIndeterminable: formValues.regulatedPublicIsIndeterminable ?? false,
    stateAgencyHasNoFiscalImpact: formValues.stateAgencyHasNoFiscalImpact ?? false,
    stateAgencyHasIncreasedCost: formValues.stateAgencyHasIncreasedCost ?? false,
    stateAgencyHasDecreasedCost: formValues.stateAgencyHasDecreasedCost ?? false,
    stateAgencyHasIncreasedRevenue: formValues.stateAgencyHasIncreasedRevenue ?? false,
    stateAgencyHasDecreasedRevenue: formValues.stateAgencyHasDecreasedRevenue ?? false,
    stateAgencyIsIndeterminable: formValues.stateAgencyIsIndeterminable ?? false,
    explanatoryDate: formValues.explanatoryDate ?? new Date(),

    accessibilityProvisionsContactPerson: formValues.accessibilityProvisionsContactPerson ?? '',
    accessibilityProvisionsPhoneNumber: formValues.accessibilityProvisionsPhoneNumber ?? '',

    agencyInfoName: formValues.agencyInfoName ?? '',
    agencyInfoDivision: formValues.agencyInfoDivision ?? '',
    agencyInfoAddress: formValues.agencyInfoAddress ??  '',
    agencyInfoCity: formValues.agencyInfoCity ?? '',
    agencyInfoState: formValues.agencyInfoState ??  '',
    agencyInfoZip: formValues.agencyInfoZip ?? '',
    agencyInfoPhone: formValues.agencyInfoPhone ?? '',

    certOfAdoptSigner: formValues.certOfAdoptSigner ?? '',
    certOfAdoptTitleOfSigner: formValues.certOfAdoptTitleOfSigner ?? '',
    certOfAdoptPublicHearingHeldOn: formValues.certOfAdoptPublicHearingHeldOn ?? new Date(),
    certOfAdoptNoticeSentOn: formValues.certOfAdoptNoticeSentOn ?? new Date(),

    // Attachement
    hearingNoticeAttachments: formValues.hearingNoticeAttachments ?? [],
    hearingSummaryAttachments: formValues.hearingSummaryAttachments ?? [],
    affidavitOfPublicationAttachments: formValues.affidavitOfPublicationAttachments ?? [],
  });

  const saveFiling = (event: any) => {
    event.preventDefault();
    if (filing?.id) {
      // existing filing selected
      let extendedData = { ...getFilingDetail(formValues) };
      let filingToSave: Filing = {
        id: filing?.id,
        agencyId: formValues.agency.id ?? filing.agencyId,
        titleId: formValues.title.id ?? filing.titleId,
        chapterId: formValues.chapter.id ?? filing.chapterId,
        filingFormId: filing.filingFormId,
        filingActionId: filing.filingActionId ?? 0,
        docketNumberId: filing.docketNumberId ?? 0,
        filingName: formValues.filingName ?? filing.filingName,
        createdBy: filing.createdBy,
        filingDetail: extendedData,
        filedOn: filing.filedOn,
        effectiveOn: formValues.effectiveOn ?? formValues.effectiveOn,
        expiresOn: formValues.expiresOn ?? filing.expiresOn,
        hearingDate: formValues.hearingDate ?? filing.hearingDate,
        publicationDate: filing.publicationDate,
        workflowId: formValues.workflow.id ?? filing.workflowId,
        workflowStatusId: status?.id ?? filing.workflowStatusId,
      };
      
          // expanded is being deleted because its a property used by the tables not something in the DB
          delete filingToSave.expanded;

      dispatch(updateFiling(filingToSave))
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            message: `${filing.filingName} was saved successfully`,
          });
        })
        .catch((error: any) => {
          addNotification({
            type: 'error',
            message: `${filing.filingName} was unable to save. ${error.message}`,
          });
        });
    } else {
      toggleConfirmationDialog();
    }
  };

  const tryCreateFiling = () => {
    let extendedData = { ...getFilingDetail(formValues) };
    let filingToSave: Filing = {
      agencyId: formValues.agency.id,
      titleId: formValues.title.id,
      chapterId: formValues.chapter.id,
      filingFormId: filingFormData.id!,
      filingActionId: 1,
      docketNumberId: 1,
      filingName: formValues.filingName,
      createdBy: '', //This will make it use the current user name for the createdby field
      filingDetail: extendedData,
      filedOn: new Date(),
      effectiveOn: formValues.effectiveOn,
      expiresOn: formValues.expiresOn,
      hearingDate: formValues.hearingDate,
      publicationDate: formValues.effectiveOn,
      workflowId: formValues.workflow.id,
      workflowStatusId: status?.id ?? filing?.workflowStatusId,
    };
    // Remove Expanded and Id, expanded is only used for the table details
    // Id can't be populated on create
    delete filingToSave.expanded;
    delete filingToSave.id;

    dispatch(createFiling(filingToSave))
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          message: `${filingToSave.filingName} was saved successfully`,
        });
        dispatch(clearDocket());
        dispatch(clearDashboardFilings());
      })
      .then(() => {
        closeFilingForm();
      })
      .catch((error: any) => {
        addNotification({
          type: 'error',
          message: `"${filingToSave.filingName}" was unable to save. Make sure all required fields are filled out. ${error.message}`,
        });
      });
  }
  //#endregion

  // #region hearing notices
  const toggleHearingNoticeForm = () => {
    newHearingNotice ? setNewHearingNotice(false) : setNewHearingNotice(true);
  };

  // #region hearing notices
  const saveHearingNotice = () => {
    if (filing) {
      let hearingNotice: HearingNotice = {
        filingId: filing.id!,
        filingActionId: formValues.action.id,
        hearingDescription: formValues.hearingDescription ?? '',
        hearingDate: formValues.hearingDate,
        hearingAddressLine1: formValues.hearingAddressLine1,
        hearingAddressLine2: formValues.hearingAddressLine2 ?? '',
        hearingCity: formValues.hearingCity,
        hearingState: formValues.hearingState,
        hearingPostalCode: formValues.hearingPostalCode,
        hearingEMeetingInfo: formValues.hearingEMeetingInfo ?? '',
        agencyHearingContactName: formValues.agencyHearingContactName ?? '',
        agencyHearingContactEmail: formValues.agencyHearingContactEmail ?? '',
        agencyHearingContactPhone: formValues.agencyHearingContactPhone ?? '',
        commentsAreLocked: false,
      };

      delete hearingNotice.id;

      dispatch(createHearingNotice(hearingNotice))
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            message: `"${formValues.hearingDescription}" was saved successfully`,
          });
          clearHearingNoticeValues();
          setNewHearingNotice(false);
        })
        .catch((error: any) => {
          addNotification({
            type: 'error',
            message: `"${formValues.hearingDescription}" was unable to save. Make sure all required fields are filled out. ${error.message}`,
          });
        });
    }
  };

  const clearHearingNoticeValues = () => {
    handleChange(formValues, 'hearingDescription', '');
    handleChange(formValues, 'hearindDate', new Date());
    handleChange(formValues, 'hearingAddressLine1', '');
    handleChange(formValues, 'hearingAddressLine2', '');
    handleChange(formValues, 'hearingCity', '');
    handleChange(formValues, 'hearingState', '');
    handleChange(formValues, 'hearingPostalCode', '');
    handleChange(formValues, 'agencyEmail', '');
    handleChange(formValues, 'additionalAgencyEmail', '');
    handleChange(formValues, 'agencyPhone', '');
  };
  // #endregion

  // #region selected form handling for dropdowns
  const getFilingActionsByDocumentType = (formName: string) => {
    var docType = documentTypes.find((item: any) => item.documentTypeName === formName);
    if (docType && docType.id) {
      dispatch(getFilingActionsById(docType?.id!));
    }
  };

  useEffect(() => {
    if (filingActions.length > 0 && !!formValues.ruleChangeTypeId) {
      formValues.ruleChangeType = filingActions.find(x => x.id === formValues.ruleChangeTypeId) ?? {};
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filingActions])
  // #endregion

  // #region status actions

  const performAction = async (filingId: number, statusActionId: number) => {
    let advanceFilingDto: AdvanceFiling = {
      filingId: filingId,
      workflowStatusActionId: statusActionId
    };

    let result: any = await filingAPI.advanceStatus(advanceFilingDto);
    getStatus(result);
    dispatch(getWorkflowStatusActionsByStatusAndRoleIds(result));
    dispatch(clearDashboardFilings());
  }

  // #endregion

  // #region read only
  const [userRoles, setUserRoles] = useState<any[]>([]);

  useEffect(() => {
    //get user roles if they do not already exist
    if (userRoles.length < 1) {
      const roleNames: any[] = JSON.parse(sessionStorage.getItem('roles') ?? "[]");
      setUserRoles(roleNames);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [roleStatusLocked, setRoleStatusLocked] = useState<boolean>(false);

  const agencyRoles = [Roles.Agency];
  const agRoles = [Roles.AgRev, Roles.AgSup];
  const gproRoles = [Roles.GovRev, Roles.GovSup];
  const sosRoles = [Roles.SosOffice];

  //statuses where the appropriate roles are able to edit the form
  const agencyStatusIds = [
    WorkflowStatuses.New,
    WorkflowStatuses.ReadyForPublicHearing,
    WorkflowStatuses.PublicHearingPending,
    WorkflowStatuses.AGReturnedToAgency,
    WorkflowStatuses.GPROReturnedToAgency,
    WorkflowStatuses.EmergencyNew,
    WorkflowStatuses.NonSubstantiveNew,
    WorkflowStatuses.EmergencyGPROReturnedToAgency,
  ];
  const agStatusIds = [WorkflowStatuses.AGPending];
  const gproStatusIds = [WorkflowStatuses.GPROPending, WorkflowStatuses.EmergencyGPROPending];
  const sosStatusIds = [WorkflowStatuses.SOSPending];

  const calculateRoleStatusLocked = () => {
    //set readOnly to true if the status is Ready For Promulgation or user is not agency user
    if (status.id === WorkflowStatuses.ReadyForPromulgation || !userRoles.some(x => agencyRoles.includes(x))) {
      return true;
    }

    //set readOnly to false if the status does not have role requirements
    if (!agencyStatusIds.includes(status.id) &&
      !agStatusIds.includes(status.id) &&
      !gproStatusIds.includes(status.id) &&
      !sosStatusIds.includes(status.id)) {
        return false;
      }

    //set readOnly to true if the status is not an agency status
    if (!agencyStatusIds.includes(status.id)) {
      return true;
    }

    //the user is an agency user and the status is an agency status
    return false;
  }

  const [agencyRoleStatusLocked, setAgencyRoleStatusLocked] = useState<boolean>(false);

  const calculateAgencyRoleStatusLocked = () => {
    return !(agencyStatusIds.includes(status.id) && userRoles.some(x => agencyRoles.includes(x)));
  }

  useEffect(() => {
    //check current user roles against filing status: some statuses have role requirements.
    if (status && userRoles.length > 0) {
      setRoleStatusLocked(calculateRoleStatusLocked());
      setAgencyRoleStatusLocked(calculateAgencyRoleStatusLocked());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, userRoles])

  // #endregion

  //#region draft preview

  const [draftPreviewHtml, setDraftPreviewHtml] = useState<string>("");

  const onDraftPreviewSelect = async (selection: string, draft: any) => {
    if (selection === "Draft Copy") {
      setDraftName(draft.draftName + ' - Draft')
      setDraftPreviewHtml(await draftAPI.getPreview(draft.id));
    }
    else if (selection === "Clean Copy") {
      setDraftName(draft.draftName + ' - Clean')
      setDraftPreviewHtml(await draftAPI.getCleanedPreview(draft.id));
    }
    toggleDraftPreview();
  }

  const [isDraftPreviewOpen, setIsDraftPreviewOpen] = useState<boolean>(false);

  const toggleDraftPreview = () => {
    setIsDraftPreviewOpen(!isDraftPreviewOpen);
  }

  //#endregion

  //#region filing notes

  const [filingNotes, setFilingNotes] = useState<any[]>([]);

  const fetchFilingNotes = async () => {
    if (filing?.id) {
      let newFilingNotes: any = await filingNoteApi.getByFilingId(filing.id);
      setFilingNotes(newFilingNotes);
    }
  }

  useEffect(() => {
    fetchFilingNotes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [addNoteFieldIsOpen, setAddNoteFieldIsOpen] = useState<boolean>(false);

  const toggleAddNoteField = () => {
    setAddNoteFieldIsOpen(!addNoteFieldIsOpen);
  }

  const [newNote, setNewNote] = useState<string>();

  const onAddNoteFieldChange = (value: any) => {
    setNewNote(value);
  }

  const createNote = async () => {
    let noteToCreate: FilingNote = {
      note: newNote ?? '',
      filingId: filing?.id ?? -1
    }
    await filingNoteApi.createFilingNote(noteToCreate).then(() => {
      fetchFilingNotes();
    });
    toggleAddNoteField();
  }

  //#endregion

  //#region delete filing

  const deleteFiling = async () => {
    await filingAPI.deleteFiling(filing?.id as number);
    toggleDeleteConfirmation();
    closeFilingForm();
  }

  const [delConfModalIsOpen, setDelConfModalIsOpen] = useState<boolean>(false);

  const toggleDeleteConfirmation = () => {
    setDelConfModalIsOpen(!delConfModalIsOpen);
  }

  //#endregion

    // #region PDF export
  const exportPDF = () => {
    if (pdfExport.current) {
      pdfExport.current.save();
    }
  };
  // #endregion

  return {
    // change handlers
    selectChange,
    handleMultiSelectChange,
    handleFormChange,
    datePickerChange,
    datePickerDateChange,
    datePickerTimeChange,
    checkBoxChange,
    // get values
    getSelectedDropDownItem,
    getDropDownValues,
    getDropdownLabelValue,
    getDropdownPrefix,
    getDropdownId,
    getMultiSelectValues,
    getFormValue,
    getCheckboxValue,
    getDropdownValueField,
    getStatus,
    // functions
    saveFiling,
    toggleHearingNoticeForm,
    saveHearingNotice,
    getFilingActionsByDocumentType,
    toggleConfirmationDialog,
    setStatusActionId,
    onDraftPreviewSelect,
    toggleDraftPreview,
    onAddNoteFieldChange,
    createNote,
    toggleAddNoteField,
    deleteFiling,
    toggleDeleteConfirmation,
    exportPDF,
    // data
    formValues,
    newHearingNotice,
    workflowSelected: draftDisabled,
    histories,
    status,
    dialogOpen,
    hearingLock,
    workflowLock,
    hearingLockFields,
    workflowLockFields,
    roleStatusLocked,
    draftPreviewHtml,
    isDraftPreviewOpen,
    agencyRoleStatusLocked,
    filingNotes,
    newNote,
    addNoteFieldIsOpen,
    savingFiling,
    delConfModalIsOpen,
    pdfExport,
    draftName,
    filingCertificateOfAdoptionDocs
  };
};
